import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Stack, Skeleton, CircularProgress, Tooltip } from '@mui/material';
import {
    RefreshCw,
    Clock3,
    CheckCircle2,
    Trash2,
    Hourglass,
    AlertCircle,
    Send,
    Search,
    SquarePen,
    Spotlight,
    Copy,
    Eye,
    BarChart3,
    Users,
    MoreHorizontal,
    Plus,
    Phone
} from 'lucide-react';
import { useReduxLeads } from '@Hooks/reduxLeadsHook/useReduxLeads';
import { useReduxProject } from '@Hooks/reduxProjectHook/useReduxProject';
import useDebounce from '@Hooks/useDebounce';
import { useInView } from 'react-intersection-observer';
import DeleteCampaignForm from '@FormForms/DeleteCampaignForm';
import UrlEncryptionService from '@Helpers/UrlEncryptionService';
import CampaignRunViewService from '@Helpers/URLHelpers/CampaignRunViewService';
import ErrorHandler from '@Root/ErrorHandler';

import './WhatsappCampaignsView.scss';

export default function WhatsappCampaignsView(props) {

    const [cursor, setCursor] = useState('auto');
    const { currentLeadsId, leadsPaymentInfo, numbersList, currentPhoneNumber } = useReduxLeads();
    const { currentProjectId } = useReduxProject();
    const [loading, setLoading] = useState(true);
    const [showCampaignDeleteForm, setShowCampaignDeleteForm] = useState(false);
    const [selectedCampaign, setSelectedCampaign] = useState(null);
    const [search, setSearch] = useState('');
    const debouncedSearch = useDebounce(search, 500, true);
    const [campaigns, setCampaigns] = useState([]);
    const [page, setPage] = useState(0);
    const [size] = useState(10);
    const [totalPages, setTotalPages] = useState(1);
    const [totalResult, setTotalResult] = useState(0);
    const [loadingMore, setLoadingMore] = useState(false);
    const [hasMore, setHasMore] = useState(true);
    const isFetchingRef = useRef(false);
    const firstRenderRef = useRef(true);
    const campaignRunViewService = new CampaignRunViewService();
    const urlEncryptionService = new UrlEncryptionService();
    const errorHandler = new ErrorHandler();

    const navigate = useNavigate();
    const className = 'whatsappCampaignsView';

    const [ref, inView] = useInView({
        triggerOnce: false,
        rootMargin: '150px'
    });

    const defaultFilter = {
        campaignRunViewFilter: {
            projectIdList: [currentProjectId],
            itemTypeList: ['whatsapp'],
            campaignRunStatusList: ['completed', 'running', 'scheduled', 'created', 'paused'],
            leadsConnectionIdList: [currentLeadsId],
            phoneNumberIdList: [currentPhoneNumber?.id],
            sortBy: 'scheduleTimeStamp',
            sortAsc: false
        },
        page: 0,
        size
    };

    useEffect(() => {
        resetAndFetch();
    }, [currentPhoneNumber?.id]);

    useEffect(() => {
        if (firstRenderRef.current) {
            firstRenderRef.current = false;
            return;
        }

        handleSearch();
    }, [debouncedSearch]);

    useEffect(() => {
        if (
            inView &&
            hasMore &&
            !loading &&
            !loadingMore
        ) {
            fetchCampaigns(page);
        }
    }, [inView]);

    const handleSearch = async () => {
        setLoading(true);
        setCampaigns([]);
        setPage(0);
        setHasMore(true);

        await fetchCampaigns(0, true);

        setLoading(false);
    };

    const resetAndFetch = async () => {
        setLoading(true);
        setCampaigns([]);
        setPage(0);
        setHasMore(true);

        await fetchCampaigns(0, true);

        setLoading(false);
    };

    const fetchCampaigns = async (
        apiPage,
        isRefresh = false
    ) => {

        if (isFetchingRef.current) return;

        isFetchingRef.current = true;

        if (!isRefresh) {
            setLoadingMore(true);
        }

        try {

            const filter = {
                ...defaultFilter,
                page: apiPage,
                size
            };

            filter.campaignRunViewFilter.searchQuery =
                debouncedSearch;

            let response =
                await campaignRunViewService.getListFiltered(filter);

            if (response.status < 400) {

                response = JSON.parse(response.body);

                const list = response.campaignRunViewList;

                setTotalPages(response.totalPages);
                setTotalResult(response.totalItems);

                setCampaigns((prev) => {

                    if (isRefresh) {
                        return list;
                    }

                    const existingIds = new Set(
                        prev.map(item => item.campaignId)
                    );

                    const filtered = list.filter(
                        item =>
                            !existingIds.has(item.campaignId)
                    );

                    return [...prev, ...filtered];
                });

                const nextPage = apiPage + 1;

                setPage(nextPage);

                setHasMore(
                    nextPage < response.totalPages
                );
            }

        } catch (err) {

            errorHandler.handleError(err);

        } finally {

            setLoadingMore(false);
            isFetchingRef.current = false;
        }
    };

    const refreshData = async () => {
        await resetAndFetch();
    };

    const toggleCampaignDeleteForm = () => {
        setShowCampaignDeleteForm(prev => !prev);
    };

    const setUpBusiness = () => {
        navigate('/home/whatsapp/business-info');
    };

    const handleCreateCampaign = () => {
        navigate('create');
    };

    const handleCampaignView = (campaign) => {

        const encryptedCampaignId =
            urlEncryptionService.encryptData(
                campaign?.campaignId
            );

        navigate(
            '/home/whatsapp/campaigns/' +
            encryptedCampaignId
        );
    };

    const handleCampaignAction = (
        campaign,
        type
    ) => {

        const encryptedCampaignId =
            urlEncryptionService.encryptData(
                campaign?.campaignId
            );

        navigate(
            encryptedCampaignId +
            '/' +
            type
        );
    };

    const getStatusBadge = (status) => {

        switch (status) {

            case 'running':
                return {
                    icon: <Clock3 size={13} />,
                    className:
                        'whatsapp-campaigns-status--running',
                    label: 'Running'
                };

            case 'completed':
                return {
                    icon: <CheckCircle2 size={13} />,
                    className:
                        'whatsapp-campaigns-status--completed',
                    label: 'Completed'
                };

            case 'draft':
                return {
                    icon: <AlertCircle size={13} />,
                    className:
                        'whatsapp-campaigns-status--draft',
                    label: 'Draft'
                };

            case 'scheduled':
                return {
                    icon: <Hourglass size={13} />,
                    className:
                        'whatsapp-campaigns-status--scheduled',
                    label: 'Scheduled'
                };

            case 'paused':
                return {
                    icon: <AlertCircle size={13} />,
                    className:
                        'whatsapp-campaigns-status--paused',
                    label: 'Paused'
                };

            case 'deleted':
                return {
                    icon: <Trash2 size={13} />,
                    className:
                        'whatsapp-campaigns-status--deleted',
                    label: 'Deleted'
                };

            default:
                return {
                    icon: null,
                    className:
                        'whatsapp-campaigns-status--default',
                    label: status || 'Unknown'
                };
        }
    };

    const getFormattedData = (date) => {

        if (!date) return '';

        return new Intl.DateTimeFormat('en-US', {
            hour: 'numeric',
            minute: '2-digit',
            day: 'numeric',
            month: 'short'
        }).format(new Date(date));
    };

    const fmt = (timestamp) => {

        if (!timestamp) return '—';

        return new Intl.DateTimeFormat('en-US', {
            year: 'numeric',
            month: 'short',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit'
        }).format(new Date(timestamp));
    };

    const CampaignSkeleton = () => (
        <div className="campaign-card campaign-card--skeleton">

            <div className="campaign-card__identity">

                <Skeleton
                    variant="circular"
                    width={42}
                    height={42}
                />

                <div>
                    <Skeleton
                        variant="text"
                        width={220}
                        height={24}
                    />

                    <Skeleton
                        variant="text"
                        width={150}
                        height={18}
                    />
                </div>

            </div>

            <div className="campaign-card__metrics">

                {[1, 2, 3, 4].map(item => (
                    <div
                        className="campaign-card__metric"
                        key={item}
                    >
                        <Skeleton
                            variant="text"
                            width={45}
                            height={24}
                        />

                        <Skeleton
                            variant="text"
                            width={65}
                            height={16}
                        />
                    </div>
                ))}

            </div>

            <div className="campaign-card__actions">
                <Skeleton
                    variant="circular"
                    width={34}
                    height={34}
                />

                <Skeleton
                    variant="circular"
                    width={34}
                    height={34}
                />
            </div>

        </div>
    );

    const renderMetric = (
        icon,
        value,
        label,
        modifier = ''
    ) => (
        <div
            className={`campaign-card__metric ${modifier}`}
        >
            <div className="campaign-card__metric-top">
                {icon}
                <span className={modifier + '__label'}>
                    {value}
                </span>
            </div>

            <span className="campaign-card__metric-label">
                {label}
            </span>
        </div>
    );

    return (
        <>
            <div
                className={className}
                style={{ cursor }}
            >
                <header className="campaigns-header">

                    <div className="campaigns-header__identity">

                        <div className="campaigns-header__icon">
                            <Spotlight size={19} />
                        </div>

                        <div>
                            <h1>
                                Campaigns
                            </h1>

                            <p>
                                Manage and monitor your WhatsApp campaigns
                            </p>
                        </div>

                    </div>

                    <div className="campaigns-header__actions">

                        <div className="campaign-search">

                            <Search size={16} />

                            <input
                                type="text"
                                placeholder="Search campaigns..."
                                value={search}
                                onChange={(e) =>
                                    setSearch(e.target.value)
                                }
                            />

                            {search && (
                                <button
                                    className="campaign-search__clear"
                                    onClick={() => setSearch('')}
                                >
                                    ×
                                </button>
                            )}

                        </div>

                        <Tooltip
                            title="Refresh campaigns"
                            arrow
                        >
                            <button
                                className="campaign-btn campaign-btn--secondary"
                                onClick={refreshData}
                            >
                                <RefreshCw size={15} />
                                <span>Refresh</span>
                            </button>
                        </Tooltip>

                        <button
                            className="campaign-btn campaign-btn--primary"
                            onClick={
                                leadsPaymentInfo?.paymentEnable
                                    ? handleCreateCampaign
                                    : setUpBusiness
                            }
                        >
                            <Plus size={16} />

                            {leadsPaymentInfo?.paymentEnable
                                ? 'New Campaign'
                                : 'Setup Business'}
                        </button>

                    </div>

                </header>

                {/* SUMMARY */}
                {
//                {!loading && campaigns.length > 0 && (
//                    <div className="campaigns-summary">
//
//                        <div className="campaigns-summary__item">
//
//                            <div className="campaigns-summary__icon">
//                                <Send size={17} />
//                            </div>
//
//                            <div>
//                                <strong>
//                                    {totalResult}
//                                </strong>
//
//                                <span>
//                                    Total campaigns
//                                </span>
//                            </div>
//
//                        </div>
//
//                        <div className="campaigns-summary__divider" />
//
//                        <div className="campaigns-summary__item">
//
//                            <div className="campaigns-summary__icon campaigns-summary__icon--green">
//                                <CheckCircle2 size={17} />
//                            </div>
//
//                            <div>
//                                <strong>
//                                    {
//                                        campaigns.filter(
//                                            c =>
//                                                c.campaignRunStatus ===
//                                                'completed'
//                                        ).length
//                                    }
//                                </strong>
//
//                                <span>
//                                    Completed
//                                </span>
//                            </div>
//
//                        </div>
//
//                        <div className="campaigns-summary__divider" />
//
//                        <div className="campaigns-summary__item">
//
//                            <div className="campaigns-summary__icon campaigns-summary__icon--blue">
//                                <Clock3 size={17} />
//                            </div>
//
//                            <div>
//                                <strong>
//                                    {
//                                        campaigns.filter(
//                                            c =>
//                                                c.campaignRunStatus ===
//                                                'running'
//                                        ).length
//                                    }
//                                </strong>
//
//                                <span>
//                                    Running
//                                </span>
//                            </div>
//
//                        </div>
//
//                    </div>
//                )}
                }
                <main className="campaigns-content">

                    {loading ? (

                        <Stack spacing={1.5}>
                            {new Array(5)
                                .fill(0)
                                .map((_, index) => (
                                    <CampaignSkeleton
                                        key={index}
                                    />
                                ))}
                        </Stack>

                    ) : campaigns.length === 0 ? (

                        <div className="campaigns-empty">

                            <div className="campaigns-empty__icon">
                                <Send size={38} />
                            </div>

                            <h3>
                                {
                                    debouncedSearch
                                        ? 'No campaigns found'
                                        : 'No WhatsApp campaigns yet'
                                }
                            </h3>

                            <p>
                                {
                                    debouncedSearch
                                        ? 'Try searching with another keyword.'
                                        : 'Create your first WhatsApp campaign to start engaging with your audience.'
                                }
                            </p>

                            {!debouncedSearch && (
                                <button
                                    className="campaign-btn campaign-btn--primary"
                                    onClick={
                                        leadsPaymentInfo?.paymentEnable
                                            ? handleCreateCampaign
                                            : setUpBusiness
                                    }
                                >
                                    <Plus size={16} />

                                    {leadsPaymentInfo?.paymentEnable
                                        ? 'Create Campaign'
                                        : 'Setup Business'}
                                </button>
                            )}

                        </div>

                    ) : (

                        <div className="campaigns-list">

                            <div className="campaigns-list__header">

                                <span>
                                    Campaign
                                </span>

                                <span>
                                    Performance
                                </span>

                                <span>
                                    Actions
                                </span>

                            </div>

                            {campaigns.map(campaign => {

                                const status =
                                    getStatusBadge(
                                        campaign.campaignRunStatus
                                    );

                                const totalSent =
                                    campaign?.totalSent || 0;

                                const delivered =
                                    campaign?.deliveredCount || 0;

                                const opened =
                                    campaign?.readCount || 0;

                                const sent =
                                    campaign?.sentCount || 0;

                                const deliveryRate =
                                    totalSent > 0
                                        ? Math.round(
                                            ((delivered + opened + sent) /
                                                totalSent) *
                                            100
                                        )
                                        : 0;

                                const openRate =
                                    delivered + opened > 0
                                        ? Math.round(
                                            (opened /
                                                (delivered + opened)) *
                                            100
                                        )
                                        : 0;

                                return (
                                    <article
                                        key={campaign.campaignId}
                                        className="campaign-card"
                                        onClick={() =>
                                            campaign.campaignRunStatus !==
                                            'deleted' &&
                                            handleCampaignView(campaign)
                                        }
                                    >

                                        {/* CAMPAIGN */}

                                        <div className="campaign-card__identity">

                                            <div className="campaign-card__avatar">
                                                <Send size={18} />
                                            </div>

                                            <div className="campaign-card__info">

                                                <div className="campaign-card__title-row">

                                                    <h3>
                                                        {campaign.campaignName}
                                                    </h3>

                                                    <span
                                                        className={`campaign-status ${status.className}`}
                                                    >
                                                        {status.icon}

                                                        {status.label}

                                                        {campaign.campaignRunStatus ===
                                                            'scheduled' && (
                                                            <>
                                                                <i />

                                                                {getFormattedData(
                                                                    campaign.scheduleTimeStamp
                                                                )}
                                                            </>
                                                        )}
                                                    </span>

                                                </div>

                                                <div className="campaign-card__meta">

                                                    <span>
                                                        <Clock3 size={13} />

                                                        {fmt(
                                                            campaign.scheduleTimeStamp
                                                        )}
                                                    </span>

                                                </div>

                                            </div>

                                        </div>

                                        {/* METRICS */}

                                        <div className="campaign-card__metrics">

                                            {renderMetric(
                                                <Send size={14} />,
                                                totalSent,
                                                'Sent',
                                                'campaign-card__metric--sent'
                                            )}

                                            {renderMetric(
                                                <CheckCircle2 size={14} />,
                                                `${opened}/${delivered + opened + sent}`,
                                                'Read/Delivered',
                                                'campaign-card__metric--success'
                                            )}

                                            {renderMetric(
                                                <BarChart3 size={14} />,
                                                `${openRate}%`,
                                                'Open rate',
                                                'campaign-card__metric--openRate'
                                            )}

                                            {renderMetric(
                                                <BarChart3 size={14} />,
                                                `${deliveryRate}%`,
                                                'Delivery rate',
                                                'campaign-card__metric--deliveryRate'
                                            )}

                                        </div>

                                        {/* ACTIONS */}

                                        <div
                                            className="campaign-card__actions"
                                            onClick={(e) =>
                                                e.stopPropagation()
                                            }
                                        >

                                            <Tooltip title="View" arrow>
                                                <button
                                                    onClick={() =>
                                                        handleCampaignView(
                                                            campaign
                                                        )
                                                    }
                                                >
                                                    <Eye size={16} />
                                                </button>
                                            </Tooltip>

                                            {(campaign.campaignRunStatus ===
                                                'scheduled' ||
                                                campaign.campaignRunStatus ===
                                                'paused') && (
                                                <Tooltip
                                                    title="Edit"
                                                    arrow
                                                >
                                                    <button
                                                        className="campaign-card__action--blue"
                                                        onClick={() =>
                                                            handleCampaignAction(
                                                                campaign,
                                                                'update'
                                                            )
                                                        }
                                                    >
                                                        <SquarePen size={16} />
                                                    </button>
                                                </Tooltip>
                                            )}

                                            <Tooltip
                                                title="Duplicate"
                                                arrow
                                            >
                                                <button
                                                    className="campaign-card__action--blue"
                                                    onClick={() =>
                                                        handleCampaignAction(
                                                            campaign,
                                                            'copy'
                                                        )
                                                    }
                                                >
                                                    <Copy size={16} />
                                                </button>
                                            </Tooltip>

                                            <Tooltip
                                                title="Delete"
                                                arrow
                                            >
                                                <button
                                                    className="campaign-card__action--danger"
                                                    onClick={() => {
                                                        setSelectedCampaign(
                                                            campaign
                                                        );
                                                        toggleCampaignDeleteForm();
                                                    }}
                                                >
                                                    <Trash2 size={16} />
                                                </button>
                                            </Tooltip>

                                        </div>

                                    </article>
                                );
                            })}

                            {/* LOAD MORE */}

                            {hasMore && (
                                <div
                                    ref={ref}
                                    className="campaigns-load-more"
                                >
                                    {loadingMore && (
                                        <>
                                            <CircularProgress
                                                size={20}
                                            />

                                            <span>
                                                Loading more campaigns...
                                            </span>
                                        </>
                                    )}
                                </div>
                            )}

                        </div>
                    )}

                </main>

            </div>

            {showCampaignDeleteForm && (
                <DeleteCampaignForm
                    open={showCampaignDeleteForm}
                    closePopup={toggleCampaignDeleteForm}
                    campaign={{
                        id: selectedCampaign?.campaignId,
                        status: selectedCampaign?.campaignRunStatus,
                        name: selectedCampaign?.campaignName
                    }}
                    reloadData={refreshData}
                />
            )}
        </>
    );
}
